import { marked } from 'marked';

/**
 * Parse markdown content to HTML
 */
export async function parseMarkdown(markdown: string): Promise<string> {
  try {
    if (!markdown) {
      console.error('Empty markdown content received');
      return '<p>No content available</p>';
    }
    
    if (markdown === 'Article content unavailable') {
      console.error('Article content unavailable message received');
      return '<p>This article is currently unavailable</p>';
    }
    
    // Clean up any markdown formatting artifacts that might have been introduced
    // This removes the "Here's your Markdown article..." prefix and any markdown code blocks
    const cleanedMarkdown = markdown
      .replace(/^Here's your Markdown article.*?```markdown\s*/s, '')
      .replace(/```\s*$/s, '');
    
    // Use marked to convert markdown to HTML
    marked.setOptions({
      gfm: true,      // GitHub flavored markdown
      breaks: true,   // Render line breaks as <br>
      silent: false   // Show warnings and errors in console
    });
    
    const html = marked.parse(cleanedMarkdown);
    
    // Verify HTML content was generated
    if (!html || typeof html !== 'string') {
      console.error('Failed to generate HTML from markdown');
      return '<p>Error rendering article content</p>';
    }
    
    return html;
  } catch (error) {
    console.error('Error parsing markdown:', error);
    return '<p>Error parsing article content</p>';
  }
}

/**
 * Calculate estimated read time for an article
 * @param content The markdown content to calculate read time for
 * @param wordsPerMinute The average reading speed in words per minute
 * @returns The estimated read time in minutes
 */
export function calculateReadTime(content: string, wordsPerMinute = 200): number {
  // Strip markdown formatting to get plain text
  const text = content
    .replace(/^Here's your Markdown article.*?```markdown\s*/s, '')
    .replace(/```\s*$/s, '')
    .replace(/[#*_~`]/g, '') // Remove markdown symbols
    .replace(/\n/g, ' ')     // Replace newlines with spaces
    .replace(/\s+/g, ' ')    // Replace multiple spaces with a single space
    .trim();
  
  // Count words
  const wordCount = text.split(' ').length;
  
  // Calculate read time in minutes, with a minimum of 1 minute
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
} 